import styled from "styled-components";

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: color-mix(in srgb, var(--brand-navy, #00212d) 55%, transparent);
  backdrop-filter: blur(4px);
`;

export const ModalContainer = styled.div`
  position: relative;
  width: min(100%, 720px);
  max-height: min(92vh, 860px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-1);
  box-shadow: var(--shadow-lg);
  color: var(--text-primary);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--brand-cta-gradient-135);
    z-index: 2;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.35rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
  background: color-mix(in srgb, var(--surface-2) 70%, transparent);
`;

export const HeaderText = styled.div`
  min-width: 0;
  flex: 1;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-heading);
  line-height: 1.25;
`;

export const ModalSubtitle = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

export const CloseButton = styled.button`
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  border: none;
  border-radius: 999px;
  background: var(--surface-3);
  color: var(--text-primary);
  font-size: 1.05rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: var(--surface-hover);
  }
`;

export const ModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 1.15rem 1.35rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.05rem;
  border-radius: 14px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-glass);
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text-heading);
  letter-spacing: 0.02em;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

export const InfoLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
`;

export const InfoValue = styled.span`
  font-size: 0.92rem;
  color: var(--text-primary);
  line-height: 1.4;
  word-break: break-word;
`;

export const DescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const DescriptionText = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.5;
  color: var(--text-primary);
  white-space: pre-wrap;
`;

export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.65rem;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ $tone }) => {
    if ($tone === "success") return "var(--success)";
    if ($tone === "danger") return "var(--danger)";
    if ($tone === "warning") return "var(--warning)";
    return "var(--info)";
  }};
  background: ${({ $tone }) => {
    if ($tone === "success") return "var(--accent-soft)";
    if ($tone === "danger") return "color-mix(in srgb, var(--danger) 18%, transparent)";
    if ($tone === "warning") return "color-mix(in srgb, var(--warning) 18%, transparent)";
    return "color-mix(in srgb, var(--info) 16%, transparent)";
  }};
  border: 1px solid
    ${({ $tone }) => {
      if ($tone === "success") return "var(--accent)";
      if ($tone === "danger") return "color-mix(in srgb, var(--danger) 35%, transparent)";
      if ($tone === "warning") return "color-mix(in srgb, var(--warning) 35%, transparent)";
      return "color-mix(in srgb, var(--info) 35%, transparent)";
    }};
`;

export const ModalFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
  padding: 1rem 1.35rem 1.2rem;
  border-top: 1px solid var(--border-subtle);
  background: color-mix(in srgb, var(--surface-2) 65%, transparent);
`;

export const FooterButton = styled.button`
  min-height: 42px;
  padding: 0.65rem 1.1rem;
  border-radius: 12px;
  border: 2px solid
    ${({ $variant }) => {
      if ($variant === "reject") {
        return "color-mix(in srgb, var(--danger) 45%, var(--border-default))";
      }
      if ($variant === "ghost") return "var(--border-default)";
      return "var(--border-default)";
    }};
  background: ${({ $variant }) => {
    if ($variant === "reject") {
      return "color-mix(in srgb, var(--danger) 16%, var(--surface-2))";
    }
    if ($variant === "ghost") return "var(--surface-overlay)";
    return "var(--accent-secondary-soft)";
  }};
  color: ${({ $variant }) => {
    if ($variant === "reject") return "var(--danger)";
    return "var(--text-heading)";
  }};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: ${({ $variant }) =>
      $variant === "reject" ? "var(--danger)" : "var(--accent)"};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 640px) {
    flex: 1 1 100%;
  }
`;
